/**
 * File: DiagnosticsPage.tsx
 * Path: apps/admin/src/routes/diagnostics/DiagnosticsPage.tsx
 * Description: System > Diagnostics (Liam, 2026-10-08): one page that says
 * whether notifications and the admin are working, and what to do when not.
 *
 * - This device: every link in the notification chain on this screen.
 * - Live test: one notification followed step by step, with timings.
 * - Whole system (admins): timers, everyone's devices, customer emails,
 *   enquiries and daily limits, from the server (runDiagnostics).
 * - Errors on this device: crashes and failures this browser recorded.
 * - Copy report: all of it as text, to paste when asking for help.
 */

import React from "react";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import type { CheckStatus } from "@idk/config";
import { Button } from "@idk/ui";
import { PageHeader } from "@/modules/layout/components/PageHeader";
import { SectionCard } from "@/shared/components/SectionCard";
import { CheckList, StatusMark } from "@/features/diagnostics/components/CheckList";
import { PushTrace } from "@/features/diagnostics/components/PushTrace";
import { useDiagnosticsPageController } from "@/features/diagnostics/hooks/useDiagnosticsPageController";
import { formatErrorTime } from "@/features/diagnostics/utils/diagnosticsViewUtils";

const Headline: React.FC<{ status: CheckStatus; text: string; checking: boolean }> = ({ status, text, checking }) => {
  return (
    <p className="flex items-center gap-stack-sm text-sm font-medium" role="status">
      {checking ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden /> : <StatusMark status={status} />}
      {text}
    </p>
  );
};

export const DiagnosticsPage: React.FC = () => {
  const { isAdmin, devices, system, facts, device, server, errors, headline, checking, runAgain, copy, clearErrors } =
    useDiagnosticsPageController();

  return (
    <div className="space-y-stack-xl">
      <PageHeader title="Diagnostics" description="Checks that notifications and the admin are working, and what to do when not.">
        <div className="flex flex-wrap gap-stack-sm">
          <Button variant="outline" onClick={runAgain}>
            <RefreshCw className="mr-stack-xs h-4 w-4" aria-hidden />
            Check again
          </Button>
          <Button onClick={copy} disabled={!facts}>
            <Copy className="mr-stack-xs h-4 w-4" aria-hidden />
            Copy report
          </Button>
        </div>
      </PageHeader>

      <div className="max-w-prose-wide space-y-stack-lg">
        <Headline status={headline.status} text={headline.text} checking={checking} />

        <SectionCard title="This device" description="Everything on this phone or computer that a notification passes through.">
          {facts ? <CheckList checks={device} /> : <p className="text-sm text-muted-foreground">Checking this device…</p>}
        </SectionCard>

        <SectionCard
          title="Live notification test"
          description="Sends you one test notification and follows it to each of your devices. Where it stops is where the problem is."
        >
          <PushTrace devices={devices.data} />
        </SectionCard>

        {isAdmin && (
          <SectionCard
            title="Whole system"
            description="Timers, everyone's notifications, customer emails and today's limits, checked on the server."
          >
            {system.loading ? (
              <p className="text-sm text-muted-foreground">Asking the server…</p>
            ) : system.error ? (
              <CheckList
                checks={[
                  {
                    id: "server",
                    label: "Server checks",
                    status: "fail",
                    detail: `Couldn't run: ${system.error}`,
                    fix: "Check this device is online and press Check again. If it keeps failing, the functions may not be deployed.",
                  },
                ]}
              />
            ) : server ? (
              <CheckList checks={server} />
            ) : null}
          </SectionCard>
        )}

        <SectionCard
          title="Errors on this device"
          description="The last crashes and failures this browser recorded. Kept on this device only."
          actions={
            errors.length > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={clearErrors}
              >
                Clear
              </Button>
            ) : undefined
          }
        >
          {errors.length === 0 ? (
            <p className="text-sm text-muted-foreground">None recorded.</p>
          ) : (
            <ul>
              {errors.map((error) => (
                <li key={`${error.at}-${error.message}`} className="border-b border-border/50 py-stack-sm text-sm last:border-b-0">
                  <p className="break-words font-mono">{error.message}</p>
                  <p className="text-muted-foreground">
                    {formatErrorTime(error.at)} on {error.path}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
};
